<template>
  <div class="evento-page">
    <div class="card evento-card">
      <div v-if="state === 'loading'" class="state-block">
        <div class="loader" />
        <p>Carregando evento...</p>
      </div>

      <div v-else-if="state === 'not_found'" class="state-block">
        <div class="state-icon">🔍</div>
        <h2>Evento não encontrado</h2>
        <p>Confira o link.</p>
      </div>

      <div v-else-if="state === 'fechado'" class="state-block">
        <div class="state-icon">{{ fechadoIcon }}</div>
        <h2>{{ eventoFechado?.nome || 'Evento' }}</h2>
        <p>{{ mensagemFechado }}</p>
        <p class="contato">Tem dúvida? Fale com a coordenação.</p>
      </div>

      <template v-else-if="state === 'form'">
        <header class="evento-header">
          <h1>{{ evento.nome }}</h1>
          <p v-if="evento.descricao" class="evento-desc">{{ evento.descricao }}</p>
          <p v-if="periodoFmt" class="evento-periodo">{{ periodoFmt }}</p>
        </header>

        <form @submit.prevent="onSubmit" class="form">
          <section class="form-section">
            <h3 class="section-title">Quem está se inscrevendo?</h3>
            <div class="form-row">
              <label class="label">Nome completo *</label>
              <input class="input" v-model="form.comprador.nome" required maxlength="120" />
            </div>
            <div class="form-grid">
              <div class="form-row">
                <label class="label">E-mail *</label>
                <input class="input" type="email" v-model="form.comprador.email" required />
              </div>
              <div class="form-row">
                <label class="label">WhatsApp *</label>
                <input class="input" type="tel" v-model="telefoneFmt" placeholder="(99) 99999-9999" required />
              </div>
            </div>
            <div class="form-row">
              <label class="label">CPF *</label>
              <input class="input" v-model="cpfFmt" placeholder="000.000.000-00" required maxlength="14" />
              <span v-if="cpfError" class="error-msg">{{ cpfError }}</span>
            </div>
          </section>

          <section v-if="evento.campos && evento.campos.length" class="form-section">
            <h3 class="section-title">Mais alguns dados</h3>
            <CamposCustomForm
              :campos="evento.campos"
              v-model="form.camposCustom"
              :errors="camposErrors"
            />
          </section>

          <section class="form-section">
            <h3 class="section-title">Escolha seu plano</h3>
            <div class="planos">
              <label v-for="p in evento.planos" :key="p.id" class="plano-card" :class="{ active: form.planoId === p.id }">
                <input type="radio" :value="p.id" v-model="form.planoId" required />
                <div class="plano-body">
                  <div class="plano-nome">{{ p.nome }}</div>
                  <div class="plano-valor">
                    <strong>{{ formatMoney(p.valor) }}</strong>
                    <span v-if="p.tipo === 'MENSAL'"> / mes <small>x {{ p.parcelas }}</small></span>
                  </div>
                </div>
              </label>
            </div>
          </section>

          <section v-if="planoSelecionado && planoSelecionado.tipo === 'UNICO'" class="form-section grupo-section">
            <h3 class="section-title">Está indo com amigas?</h3>
            <p v-if="!form.convidados.length" class="grupo-hint">
              Vai levar amigas? Você paga uma única cobrança pelo grupo (até {{ MAX_CONVIDADOS }} convidadas).
            </p>
            <div v-for="(c, i) in form.convidados" :key="i" class="convidado-row">
              <div class="convidado-num">{{ i + 2 }}</div>
              <input class="input" v-model="c.nome" :placeholder="`Nome da ${i + 1}ª amiga`" maxlength="120" required />
              <button type="button" class="btn-remover" @click="removerConvidado(i)" aria-label="Remover">✕</button>
            </div>
            <button v-if="form.convidados.length < MAX_CONVIDADOS" type="button" class="btn-add-convidado" @click="adicionarConvidado">
              + Adicionar amiga
            </button>
          </section>

          <section v-if="planoSelecionado && planoSelecionado.tipo === 'UNICO' && breakdownSelecionado" class="form-section">
            <div class="breakdown-box">
              <div class="breakdown-row">
                <span>Inscrição
                  <small v-if="tamanhoGrupo > 1"> ({{ tamanhoGrupo }} × {{ formatMoney(valorPlanoAjustado) }})</small>
                </span>
                <span>{{ formatMoney(breakdownSelecionado.valorPlanoTotal) }}</span>
              </div>
              <div class="breakdown-row">
                <span>Taxa de serviço</span>
                <span>{{ formatMoney(breakdownSelecionado.comissaoPlataforma + breakdownSelecionado.taxaGateway) }}</span>
              </div>
              <div class="breakdown-row total">
                <span>Total (PIX)</span>
                <strong>{{ formatMoney(breakdownSelecionado.total) }}</strong>
              </div>
            </div>
          </section>

          <section v-if="planoSelecionado && planoSelecionado.tipo === 'UNICO'" class="form-section">
            <button v-if="!showCupom" type="button" class="link-btn" @click="showCupom = true">
              Tenho um cupom de desconto
            </button>
            <div v-else class="cupom-box">
              <label class="label">Código do cupom</label>
              <div class="cupom-row">
                <input class="input cupom-input" v-model="cupomCodigo"
                       @input="cupomCodigo = cupomCodigo.toUpperCase().replace(/[^A-Z0-9_-]/g, '')"
                       placeholder="EX: FAMILIA10" maxlength="40" />
                <button v-if="!cupomAplicado" type="button" class="btn-secondary btn-aplicar" :disabled="!cupomCodigo || validandoCupom" @click="aplicarCupom">
                  {{ validandoCupom ? '...' : 'Aplicar' }}
                </button>
                <button v-else type="button" class="btn-secondary" @click="removerCupom">Remover</button>
              </div>
              <div v-if="cupomErro" class="error-msg">{{ cupomErro }}</div>
              <div v-if="cupomAplicado" class="cupom-aplicado">
                ✓ Cupom <strong>{{ cupomAplicado.cupom.codigo }}</strong>:
                de {{ formatMoney(cupomAplicado.valorOriginal) }} por <strong>{{ formatMoney(cupomAplicado.valorFinal) }}</strong>
                <small>(economia {{ formatMoney(cupomAplicado.desconto) }})</small>
              </div>
            </div>
          </section>

          <div v-if="serverError" class="error-banner">{{ serverError }}</div>

          <button type="submit" class="btn-primary btn-submit" :disabled="submitting || !canSubmit">
            <span v-if="submitting">Processando...</span>
            <span v-else>Continuar para pagamento</span>
          </button>
        </form>
      </template>
    </div>
  </div>
</template>

<script>
import { getEventoPublico, criarInscricao, validarCupom } from '../../lib/inscricao'
import { formatCpf, formatPhone, isValidCpf, digitsOnly } from '../../lib/cpf'
import { calcularBreakdownGrupo, MAX_CONVIDADOS } from '../../lib/breakdownGrupo'
import CamposCustomForm from '../../components/public/CamposCustomForm.vue'

export default {
  name: 'EventoPage',
  components: { CamposCustomForm },
  data() {
    return {
      state: 'loading',
      evento: null,
      eventoFechado: null,
      mensagemFechado: 'Inscrições encerradas.',
      submitting: false,
      serverError: '',
      cpfError: '',
      camposErrors: {},
      form: {
        comprador: { nome: '', email: '', cpf: '', telefone: '' },
        planoId: '',
        camposCustom: {},
        convidados: []
      },
      MAX_CONVIDADOS,
      showCupom: false,
      cupomCodigo: '',
      cupomAplicado: null,
      cupomErro: '',
      validandoCupom: false
    }
  },
  computed: {
    cpfFmt: {
      get() { return formatCpf(this.form.comprador.cpf) },
      set(v) { this.form.comprador.cpf = digitsOnly(v) }
    },
    telefoneFmt: {
      get() { return formatPhone(this.form.comprador.telefone) },
      set(v) { this.form.comprador.telefone = digitsOnly(v) }
    },
    periodoFmt() {
      if (!this.evento?.dataInicio) return null
      try {
        const i = new Date(this.evento.dataInicio).toLocaleDateString('pt-BR')
        if (!this.evento.dataFim) return i
        const f = new Date(this.evento.dataFim).toLocaleDateString('pt-BR')
        return `${i} a ${f}`
      } catch { return null }
    },
    canSubmit() {
      return this.form.comprador.nome.trim().length >= 2
        && this.form.comprador.email.includes('@')
        && digitsOnly(this.form.comprador.telefone).length >= 10
        && this.form.comprador.cpf.length === 11
        && this.form.planoId
        && this.convidadosValidos
    },
    planoSelecionado() {
      return this.evento?.planos?.find(p => p.id === this.form.planoId) || null
    },
    tamanhoGrupo() {
      return 1 + (this.form.convidados?.length || 0)
    },
    valorPlanoAjustado() {
      const p = this.planoSelecionado
      if (!p) return 0
      return this.cupomAplicado
        ? Math.max(0, p.valor - this.cupomAplicado.desconto)
        : p.valor
    },
    breakdownSelecionado() {
      const p = this.planoSelecionado
      if (!p || p.tipo !== 'UNICO') return null
      return calcularBreakdownGrupo(this.valorPlanoAjustado, 'PIX', this.tamanhoGrupo, this.evento?.comissao)
    },
    convidadosValidos() {
      return this.form.convidados.every(c => (c.nome || '').trim().length >= 2)
    },
    fechadoIcon() {
      const s = this.eventoFechado?.status
      if (s === 'LOTADO') return '🎫'
      if (s === 'RASCUNHO') return '🚧'
      if (s === 'ENCERRADO') return '🔒'
      if (s === 'ARQUIVADO') return '📦'
      return '🔒'
    }
  },
  async mounted() {
    await this.load()
  },
  watch: {
    'form.planoId'() {
      this.removerCupom()
      if (this.planoSelecionado?.tipo !== 'UNICO') this.form.convidados = []
    }
  },
  methods: {
    async load() {
      this.state = 'loading'
      try {
        this.evento = await getEventoPublico(this.$route.params.slug)
        this.state = 'form'
      } catch (err) {
        if (err.response?.status === 410) {
          this.eventoFechado = err.response.data.evento
          this.mensagemFechado = err.response.data.message || 'Inscricoes encerradas.'
          this.state = 'fechado'
        } else if (err.response?.status === 404) {
          this.state = 'not_found'
        } else {
          this.serverError = 'Falha ao carregar evento. Tente novamente.'
          this.state = 'form'
        }
      }
    },
    formatMoney(v) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v)
    },
    validarCpf() {
      if (!isValidCpf(this.form.comprador.cpf)) {
        this.cpfError = 'CPF invalido'
        return false
      }
      this.cpfError = ''
      return true
    },
    async aplicarCupom() {
      this.cupomErro = ''
      if (!this.form.planoId) {
        this.cupomErro = 'Escolha um plano antes de aplicar o cupom.'
        return
      }
      this.validandoCupom = true
      try {
        const res = await validarCupom({
          eventoSlug: this.$route.params.slug,
          planoId: this.form.planoId,
          codigo: this.cupomCodigo,
          cpf: this.form.comprador.cpf || undefined
        })
        if (res.valido) {
          this.cupomAplicado = res
        } else {
          this.cupomErro = res.message || 'Cupom invalido'
        }
      } catch (err) {
        this.cupomErro = err.response?.data?.message || 'Cupom invalido'
        this.cupomAplicado = null
      } finally {
        this.validandoCupom = false
      }
    },
    removerCupom() {
      this.cupomAplicado = null
      this.cupomErro = ''
    },
    adicionarConvidado() {
      if (this.form.convidados.length >= MAX_CONVIDADOS) return
      this.form.convidados.push({ nome: '' })
    },
    removerConvidado(i) {
      this.form.convidados.splice(i, 1)
    },
    async onSubmit() {
      this.serverError = ''
      if (!this.validarCpf()) return
      this.submitting = true
      try {
        const payload = {
          eventoSlug: this.$route.params.slug,
          planoId: this.form.planoId,
          comprador: { ...this.form.comprador },
          camposCustom: this.form.camposCustom,
          convidados: this.form.convidados
            .map(c => ({ nome: (c.nome || '').trim() }))
            .filter(c => c.nome.length >= 2),
          ...(this.cupomAplicado && { cupomCodigo: this.cupomAplicado.cupom.codigo })
        }
        const res = await criarInscricao(payload)
        this.$router.push(`/checkout/${res.inscricaoId}`)
      } catch (err) {
        const status = err.response?.status
        const data = err.response?.data
        if (status === 409 && data?.code === 'CPF_DUPLICADO') {
          this.serverError = 'Esse CPF já está inscrito nesse evento.'
        } else if (status === 409 && data?.code === 'EVENTO_LOTADO') {
          this.eventoFechado = { ...this.evento, status: 'LOTADO' }
          this.mensagemFechado = data?.message || 'Vagas esgotadas.'
          this.state = 'fechado'
        } else if (status === 410) {
          this.eventoFechado = this.evento
          this.mensagemFechado = data?.message || 'Inscricoes encerradas.'
          this.state = 'fechado'
        } else if (status === 400 && Array.isArray(data?.errors)) {
          this.camposErrors = {}
          for (const e of data.errors) this.camposErrors[e.field] = e.message
          this.serverError = data.message || 'Verifique os campos.'
        } else if (status === 429) {
          this.serverError = data?.message || 'Muitas tentativas. Aguarde 1 minuto.'
        } else {
          this.serverError = data?.message || 'Falha ao inscrever. Tente novamente.'
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.evento-page {
  min-height: calc(100vh - 60px);
  display: flex; justify-content: center; align-items: flex-start;
  padding: var(--spacing-lg) var(--spacing-base);
}
.evento-card {
  width: 100%; max-width: 720px;
  padding: var(--spacing-xl);
}
.state-block {
  display: flex; flex-direction: column; align-items: center; gap: var(--spacing-md);
  padding: var(--spacing-xl) 0; text-align: center;
}
.state-icon { font-size: 56px; }
.contato { font-size: var(--text-sm); color: var(--color-text-muted); margin-top: var(--spacing-md); }
.evento-header { margin-bottom: var(--spacing-lg); padding-bottom: var(--spacing-md); border-bottom: 1px solid var(--color-bg-separator); }
.evento-header h1 { font-size: var(--text-2xl); margin-bottom: var(--spacing-xs); }
.evento-desc { color: var(--color-text-secondary); margin-bottom: var(--spacing-sm); line-height: 1.5; white-space: pre-line; }
.evento-periodo { color: var(--color-text-muted); font-size: var(--text-sm); }
.form { display: flex; flex-direction: column; gap: var(--spacing-lg); }
.form-section { display: flex; flex-direction: column; gap: var(--spacing-md); }
.section-title { font-size: var(--text-md); font-weight: 700; color: var(--color-text-heading); }
.form-row { display: flex; flex-direction: column; gap: 4px; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-md); }
@media (max-width: 540px) {
  .form-grid { grid-template-columns: 1fr; }
  .evento-card { padding: var(--spacing-base); }
}
.planos { display: grid; grid-template-columns: 1fr; gap: var(--spacing-sm); }
.plano-card {
  display: flex; align-items: center; gap: var(--spacing-md);
  padding: var(--spacing-base);
  border: 2px solid var(--color-bg-separator);
  border-radius: var(--radius-md);
  cursor: pointer; transition: var(--transition-default);
}
.plano-card:hover { border-color: var(--color-accent); }
.plano-card.active {
  border-color: var(--color-accent);
  background: rgba(37,99,235,0.04);
}
.plano-card input { accent-color: var(--color-accent); }
.plano-body { display: flex; flex-direction: column; gap: 2px; flex: 1; }
.plano-nome { font-weight: 600; }
.plano-valor strong { color: var(--color-accent); font-size: var(--text-md); }
.plano-valor small { color: var(--color-text-muted); }
.link-btn {
  background: none; border: none; color: var(--color-accent);
  cursor: pointer; text-decoration: underline; font-size: var(--text-sm);
  padding: 4px 0;
}
.cupom-box {
  background: var(--color-bg-hover);
  border-radius: var(--radius-md);
  padding: var(--spacing-md);
  display: flex; flex-direction: column; gap: var(--spacing-sm);
}
.cupom-row { display: flex; gap: var(--spacing-sm); }
.cupom-input { flex: 1; text-transform: uppercase; font-family: monospace; }
.btn-aplicar { white-space: nowrap; }
.cupom-aplicado {
  background: #dcfce7; color: #166534;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
}
.cupom-aplicado small { color: #166534; opacity: 0.8; margin-left: 4px; }
.error-banner {
  background: #fee2e2; color: #b91c1c;
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
}
.grupo-section { gap: var(--spacing-sm); }
.grupo-hint {
  font-size: var(--text-sm); color: var(--color-text-secondary); margin: 0;
}
.convidado-row { display: flex; align-items: center; gap: var(--spacing-sm); }
.convidado-num {
  width: 28px; height: 28px; border-radius: 999px;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700;
  background: var(--color-bg-hover); color: var(--color-text-secondary);
  flex-shrink: 0;
}
.convidado-row .input { flex: 1; }
.btn-remover {
  width: 32px; height: 32px; flex-shrink: 0;
  background: transparent; border: 1px solid var(--color-bg-separator);
  border-radius: var(--radius-sm); cursor: pointer;
  color: var(--color-text-muted); font-family: inherit;
}
.btn-remover:hover { color: var(--color-danger, #dc2626); border-color: var(--color-danger, #dc2626); }
.btn-add-convidado {
  align-self: flex-start;
  background: transparent; color: var(--color-accent);
  border: 1px dashed var(--color-accent);
  padding: 8px 14px; font-size: var(--text-sm); font-weight: 600;
  border-radius: var(--radius-sm); cursor: pointer;
  font-family: inherit;
}
.btn-add-convidado:hover { background: rgba(37,99,235,0.05); }
.breakdown-box {
  background: var(--color-bg-hover);
  border-radius: var(--radius-md);
  padding: var(--spacing-md);
  display: flex; flex-direction: column; gap: 6px;
  font-size: var(--text-sm);
}
.breakdown-row { display: flex; justify-content: space-between; color: var(--color-text-secondary); }
.breakdown-row.total {
  color: var(--color-text-heading); font-size: var(--text-md); font-weight: 700;
  padding-top: 6px; border-top: 1px solid var(--color-bg-separator);
}
.btn-submit { padding: 14px; font-size: var(--text-md); }
.loader {
  width: 28px; height: 28px;
  border: 3px solid var(--color-bg-separator);
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
